import React, {ComponentProps, useMemo} from 'react'

import {BooleanMatchingFilter, Filter, FilterParams, getCount} from '../../libs/table-row-filter'

import Button from '../../../../commons/src/components/button'
import FilterSelect from '../../../../commons/src/components/filter-select'
import FilterSelectFlyout from '../../../../commons/src/components/filter-select-flyout'
import RadioGroupField from '../../../../commons/src/components/radio-group-field'
import RadioGroupItem from '../../../../commons/src/components/radio-group-field/RadioGroupItem'

type FilterSelectProps = ComponentProps<typeof FilterSelect>

type Props<TableRow> = {
  label: string
  options: {undefined: string; true: string; false: string}
  filterKey: string
  filterMap: Record<string, Filter<TableRow, FilterParams<TableRow>>>
  tableRows: TableRow[]
  applyFilter: (newFilter: BooleanMatchingFilter<TableRow>) => void
  size?: FilterSelectProps['size']
}

const BooleanMatchingFilterPartial = <TableRow,>({
  label,
  options,
  filterKey,
  filterMap,
  tableRows,
  applyFilter,
  size = '100',
}: Props<TableRow>) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const filter = filterMap[filterKey] as BooleanMatchingFilter<TableRow>

  const [filterValue, setFilterValue] = React.useState<boolean | undefined>(
    filter.serialize().filterValue
  )

  const keyToValue = (optionKey: string) => ({undefined, true: true, false: false})[optionKey]

  const getCountFor = (newFilterValue?: boolean) => {
    const newFilter = new BooleanMatchingFilter({
      ...filter.serialize(),
      filterValue: newFilterValue,
    })
    return getCount(tableRows, filterMap, filterKey, newFilter)
  }

  const memoizedCounts = useMemo(
    () =>
      Object.keys(options).reduce((acc: Record<string, number>, optionKey: string) => {
        const option = keyToValue(optionKey)
        acc[optionKey] = getCountFor(option)
        return acc
      }, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, tableRows, filterMap]
  )

  React.useEffect(() => {
    if (isOpen) {
      setFilterValue(filter.serialize().filterValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleClear = () => {
    applyFilter(
      new BooleanMatchingFilter({
        ...filter.serialize(),
        filterValue: undefined,
      })
    )
    setFilterValue(undefined)
    setIsOpen(false)
  }

  const handleSubmit = () => {
    applyFilter(
      new BooleanMatchingFilter({
        ...filter.serialize(),
        filterValue,
      })
    )
    setIsOpen(false)
  }

  return (
    <FilterSelect
      label={filter.getLabel(label)}
      size={size}
      active={filter.isActive()}
      isOpen={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      onShouldClose={() => setIsOpen(false)}
      menu={
        <FilterSelectFlyout
          onClear={handleClear}
          clearLabel="Clear"
          submitButton={
            <Button
              block
              onClick={() => {
                handleSubmit()
              }}
            >
              Apply ({getCountFor(filterValue)})
            </Button>
          }
        >
          <RadioGroupField
            className="u-no-margin-bottom"
            value={`${filterValue}`}
            onChange={value => {
              if (value) {
                setFilterValue(keyToValue(value))
              } else {
                setFilterValue(undefined)
              }
            }}
          >
            {Object.keys(options).map(optionKey => (
              <RadioGroupItem
                key={optionKey}
                label={options[optionKey as keyof typeof options]}
                value={optionKey}
                count={memoizedCounts[optionKey]}
              />
            ))}
          </RadioGroupField>
        </FilterSelectFlyout>
      }
    />
  )
}

export default BooleanMatchingFilterPartial
